<template>
  <section id="add-account-form">
    <v-row class="d-flex justify-center" no-gutters>
      <v-col cols="12" md="10" lg="10">
        <page-header
          v-if="!showForm"
          class="mb-8"
          :breadcrumbs="breadcrumbs"
          :title="accountId ? pageTitle : $dictionary.app.account.create.title"
        />
        <v-card class="transparent" flat>
          <v-form
            v-model="isFormValid"
            ref="accountForm"
            @submit.prevent="updateAccountDetails"
          >
            <card-widget>
              <template #cardBody>
                <!---------------------------- ADD ACCOUNT FORM STARTS ---------------------------->
                <v-row class="pb-4" no-gutters>
                  <v-col cols="12" md="12">
                    <h6 class="text-h6 font-weight-large secondary--font">
                      {{ formConfig.title }}
                    </h6>
                  </v-col>
                </v-row>
                <v-row no-gutters v-if="!showForm">
                  <v-col md="12" cols="12">
                    <div class="subtitle-1 mb-6" v-if="accountId">
                      Account ID {{ accountId }}
                    </div>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col md="6" cols="12" class="pt-0">
                    <!-- Todo add async validation to validate the account name user have entered -->
                    <v-text-field
                      v-model="accountDetails.name"
                      label="Account Name"
                      class="required"
                      outlined
                      flat
                      :rules="[
                        required('Account Name'),
                        maxLength(255, 'Account Name'),
                      ]"
                  /></v-col>
                  <v-col md="6" cols="12" class="pt-0">
                    <v-select
                      v-model="accountDetails.status"
                      label="Status"
                      class="required"
                      outlined
                      flat
                      :items="statusOptions"
                      maxlength="255"
                      :rules="[required('Status')]"
                    >
                    </v-select>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col md="6" cols="12" class="pt-0">
                    <v-text-field
                      v-model="accountDetails.address_line_1"
                      label="Address Line 1"
                      outlined
                      :rules="[maxLength(255, 'Address Line 1', false)]"
                    />
                  </v-col>
                  <v-col md="6" cols="12" class="pt-0">
                    <v-text-field
                      v-model="accountDetails.address_line_2"
                      label="Address Line 2"
                      outlined
                      :rules="[maxLength(255, 'Address Line 2', false)]"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col md="6" cols="12" class="pt-0">
                    <v-autocomplete
                      :items="countries"
                      v-model="accountDetails.country"
                      item-text="label"
                      item-value="value"
                      label="Country"
                      no-data-text="No countries available"
                      @change="getCountryStates"
                      outlined
                    />
                  </v-col>
                  <v-col md="6" cols="12" class="pt-0">
                    <v-autocomplete
                      :items="states"
                      item-text="label"
                      item-value="value"
                      v-model="accountDetails.state"
                      label="State/Province"
                      no-data-text="No state/province available"
                      outlined
                      :disabled="areStatesLoading"
                      :loading="areStatesLoading"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col md="6" cols="12" class="py-0">
                    <v-text-field
                      v-model="accountDetails.city"
                      label="City"
                      outlined
                      :rules="[maxLength(255, 'City', false)]"
                    />
                  </v-col>
                  <v-col md="6" cols="12" class="py-0">
                    <v-text-field
                      v-model="accountDetails.zipcode"
                      label="Zip/Postal Code"
                      outlined
                      max="100"
                      :rules="[
                        maxLength(255, 'Zip/Postal Code', false),
                        alphaNumeric('Zip/Postal Code'),
                      ]"
                    >
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col md="6" cols="12" class="py-0">
                    <v-text-field
                      v-model="accountDetails.maropost_account_id"
                      label="Maropost Account Id"
                      outlined
                      max="100"
                      @keypress="onlyNumber"
                      :rules="[numeric('Maropost Account Id')]"
                    >
                    </v-text-field>
                  </v-col>
                </v-row>

                <!---------------------------- ACCOUNT SETTINGS FORM ENDS ---------------------------->
              </template>
            </card-widget>

            <v-divider class="mt-8 mb-6 border-light-grey" />
            <v-row no-gutters class="d-flex justify-start">
              <v-col cols="12">
                <v-btn
                  type="submit"
                  id="save-account-settings"
                  medium
                  :loading="isLoading"
                  color="primary"
                  :disabled="!isFormValid"
                  class="px-4 button font-weight-bold white-btn-text"
                >
                  Save Changes
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
          <loader :loading="isLoading" :size="$appConfig.loader.small" />
        </v-card>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import clone from "lodash/clone";
import {
  createAccount,
  updateAccount,
  getAccount,
  getCustomer,
} from "@/services";
import { CardWidget } from "@/components/widgets";
import { isResponseCode } from "@/utils/common.utils";
import { RESPONSE_CODES, ACCOUNT_STATUS_OPTIONS } from "@/constants";
import { PageHeader } from "@/components/shared";
import { mapGetters, mapActions } from "vuex";
import { onlyNumber } from "@/utils/common.utils";
import {
  numeric,
  required,
  maxLength,
  alphaNumeric,
  maxLengthNumber,
} from "@/validators/form-validators";

/**
 * Add Account form
 */
export default {
  name: "AddAccount",
  /**
   * Custom events
   */
  emits: ["set-account"],
  /**
|--------------------------------------------------
| Props
|--------------------------------------------------
*/
  props: {
    showForm: { type: Boolean, default: false },
  },
  /**
   * ---------------- Components ------------------
   */
  components: {
    CardWidget,
    PageHeader,
  },
  /**
   * ---------------- Data properties ------------------
   */
  data() {
    const accountform = this.$dictionary.app.account.create.addAccountForm;
    return {
      isFormValid: false,
      cities: [],
      customer: {},
      accountDetails: {
        name: "",
        website: "",
        address_line_1: "",
        address_line_2: "",
        zipcode: "",
        city: "",
        state: "",
        parent_id: "",
        country: "",
        status: "active",
        maropost_account_id: "",
      },
      statusOptions: ACCOUNT_STATUS_OPTIONS,
      areStatesLoading: false,
      isLoading: false,
      formConfig: accountform,
      status: null,
    };
  },
  /**
   * ---------------- Methods ------------------
   */
  methods: {
    required,
    maxLength,
    maxLengthNumber,
    numeric,
    alphaNumeric,
    onlyNumber,
    ...mapActions({
      getCountries: "app/getCountries",
      getStates: "app/getStates",
      setSnackbar: "ui/setSnackbar",
    }),

    /**
     * update isLoading attribute value
     */
    setIsLoading(val) {
      this.isLoading = val;
    },
    /**
     * Fetched states of a country
     * @param {String} country Country whose states are to be fetched
     */
    async getCountryStates(country) {
      this.areStatesLoading = true;
      await this.getStates(country);
      this.areStatesLoading = false;

      this.accountDetails.state = "";
    },

    /**
     * Convert Threshold value to string
     * @param {String} threshold
     */
    getThreshold: (threshold) => (threshold ? String(threshold) : "0"),

    /**
     * Creates or update the account details
     */
    async updateAccountDetails() {
      try {
        if (!this.isFormValid) {
          return this.$refs.accountForm.validate();
        }

        this.setIsLoading(true);
        let response;
        let successMsg;
        if (!this.accountId) {
          response = await createAccount(this.payload);
          successMsg = this.formConfig.successMsg;
        } else {
          response = await updateAccount({
            account_id: this.accountId,
            ...this.payload,
          });
          successMsg = this.$dictionary.app.account.update.editAccountForm
            .successMsg;
        }

        if (isResponseCode(response.status, RESPONSE_CODES.success)) {
          this.setSnackbar({
            value: true,
            message: successMsg,
            type: this.snackbarTypes.success,
          });
          this.redirectToIndex();
        }
      } catch (error) {
        !this.storeSnackbar.value &&
          this.setSnackbar({
            value: true,
            message: error?.message,
            type: this.snackbarTypes.error,
          });
      } finally {
        this.setIsLoading(false);
      }
    },
    /**
     * Redirect to index page
     */
    redirectToIndex() {
      if (!this.showForm) {
        this.$router.push({
          name: "customer",
          params: { id: this.customerId },
          query: { active_tab: "accounts" },
        });
      }
    },
    /**
     * Fetches account details and spread them in the form
     */
    async fetchAccountDetails() {
      try {
        this.setIsLoading(true);
        const { data } = await getAccount({ account_id: this.accountId });
        if (data?.country) await this.getCountryStates(data.country);

        this.accountDetails = data ?? {};
        this.$emit("set-account", data);
      } catch (error) {
        !this.storeSnackbar.value &&
          this.setSnackbar({
            value: true,
            message: error?.message,
            type: this.snackbarTypes.error,
          });
      } finally {
        this.setIsLoading(false);
      }
    },

    /**
     * Get customer details
     */
    async getCustomerDetails() {
      try {
        this.setIsLoading(true);
        const { data } = await getCustomer(this.customerId);
        this.customer = data || [];
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: this.$errorMessage(error),
          type: this.snackbarTypes.error,
        });
      } finally {
        this.setIsLoading(false);
      }
    },
    /**
     * Fetches all the required data from api resources
     * @description Stops the execution once all the promises are resolved
     */
    async fetchData() {
      this.setIsLoading(true);
      const resources = [
        this.getCountries(),
        this.accountId && this.fetchAccountDetails(),
        !this.showForm && this.getCustomerDetails(),
      ];
      await Promise.all(resources);
      this.setIsLoading(false);
    },
  },
  /**
   * ---------------- Computed properties ------------------
   */
  computed: {
    ...mapGetters({
      countries: "app/countries",
      states: "app/states",
      storeSnackbar: "ui/snackbar",
    }),

    /**
     * Create or update account settings request payload
     * @type {Object}
     */
    payload() {
      const {
        inbox_threshold,
        spam_threshold,
        missing_threshold,
        ...formData
      } = clone(this.accountDetails);

      return {
        ...formData,
        inbox_threshold: this.getThreshold(inbox_threshold),
        spam_threshold: this.getThreshold(spam_threshold),
        missing_threshold: this.getThreshold(missing_threshold),
        customer_id: this.customerId,
      };
    },

    /**
     * Snackbar types
     */
    snackbarTypes() {
      return this.$appConfig.snackbar.snackbarTypes;
    },
    /**
     * Accounts page breadcrumbs
     * @type {Array}
     */
    breadcrumbs() {
      return [
        {
          title: this.$dictionary.app.customers.index.title,
          type: this.$appConfig.breadcrumbs.types.route,
          to: this.$paths.customers,
        },
        {
          title: this.customer?.name,
          type: this.$appConfig.breadcrumbs.types.route,
          to: `${this.$paths.customers}/${this.customer?.id}/customer?active_tab=accounts`,
        },
        {
          title: this.accountId
            ? this.pageTitle
            : this.$dictionary.app.account.create.title,
          type: this.$appConfig.breadcrumbs.types.route,
        },
      ];
    },

    /**
     * get Page title
     */
    pageTitle() {
      return `${this.accountDetails.name}`;
    },

    /**
     * Account ID
     */
    accountId() {
      return this.$route.params.id;
    },

    /**
     * Get customer ID
     */
    customerId() {
      return this.$route.params?.customerId;
    },
  },
  /**
   * --------Created lifecycle hook -------------
   */
  created() {
    this.fetchData();
  },
};
</script>
